import type { Researcher, FullCycle, Cycle, Event, ExportRecord, WorkflowJob, AISuggestion, TelemetryPoint } from './types'
import { nanoid } from 'nanoid'

// === LOCAL-ONLY CLIENT API (static Pages deploy graceful fallback) ===
// Full sovereign local persistence via localStorage. No network /api calls.
// Rebuilds the core cognitive instrument (PAOU loop + Ambiguity Tolerance Curve) as a fully usable SPA artifact.
// Real D1/KV/R2/Queues/AE/AI/Worker only in full Cloudflare worker deploy (resources stubbed here).
// All original function signatures preserved. Seed demo works. Exports (self-html), curve, history, privacy all work.

const LS_KEY = 'ambit_local_v1'

type LocalState = {
  researchers: Record<string, any>
  cycles: Record<string, any>
  claims: Record<string, any>
  predictions: Record<string, any>
  actions: Record<string, any>
  observations: Record<string, any>
  updates: Record<string, any>
  events: any[]
  exportsRec: Record<string, any>
  sessions: Record<string, string> // token -> rid
  aiSuggestions: Record<string, any>
}

function loadState(): LocalState {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return {
    researchers: {}, cycles: {}, claims: {}, predictions: {}, actions: {},
    observations: {}, updates: {}, events: [], exportsRec: {}, sessions: {},
    aiSuggestions: {}
  }
}

function saveState(s: LocalState) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(s)) } catch {}
}

async function deriveResearcherId(passphrase: string, salt: string): Promise<string> {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${passphrase}::${salt}`))
      return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 28)
    }
  } catch {}
  // simple deterministic fallback
  let h = 2166136261
  const str = `${passphrase}::${salt}`
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return Math.abs(h).toString(36).padStart(28, '0').slice(0, 28)
}

function getOrCreateResearcher(state: LocalState, id: string, handle: string) {
  const now = Date.now()
  if (!state.researchers[id]) {
    state.researchers[id] = {
      id, handle: handle.slice(0, 80), created_at: now, updated_at: now, telemetry_consent: 0
    }
  }
  return state.researchers[id]
}

function issueSession(state: LocalState, rid: string): string {
  const token = nanoid(28)
  state.sessions[token] = rid
  return token
}

function validate(token: string | null, ridHeader: string | null): string | null {
  const state = loadState()
  if (!token) return null
  const stored = state.sessions[token]
  if (!stored) return null
  if (ridHeader && stored !== ridHeader) return null
  return stored
}

function getRidFrom(token: string, ridHeader?: string) {
  const rid = validate(token, ridHeader || null)
  if (!rid) throw new Error('unauthorized')
  return rid
}

function recordEvent(state: LocalState, rid: string, type: string, payload: any = {}) {
  state.events.unshift({ id: Date.now() + Math.random(), researcher_id: rid, type, payload_json: JSON.stringify(payload), created_at: Date.now() })
  if (state.events.length > 180) state.events.length = 180
}

function computeToleranceIndex(hBefore: number, hAfter: number, systemRecog: number, surprise: number, learningLen: number): number {
  const helplessnessDelta = Math.max(-6, Math.min(6, (hBefore - hAfter)))
  const sys = Math.max(0, Math.min(10, systemRecog))
  const nuance = Math.min(3, Math.floor(learningLen / 80))
  return Math.round((helplessnessDelta * 0.9) + (sys * 0.7) + nuance + (surprise > 5 ? 0.5 : 0))
}

function ensureCycleTables(state: LocalState, cycleId: string) {
  // noop, we use direct records
}

export async function unlock(passphrase: string, handle: string) {
  const salt = 'ambit-rumor-resistance-2026'
  const rid = await deriveResearcherId(passphrase, salt)
  const state = loadState()
  const r = getOrCreateResearcher(state, rid, handle)
  const token = issueSession(state, rid)
  saveState(state)
  return { researcher: { id: r.id, handle: r.handle, telemetry_consent: r.telemetry_consent || 0 }, token }
}

export async function getMe(token: string, rid: string) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  const r = state.researchers[realRid]
  if (!r) throw new Error('not_found')
  return { id: r.id, handle: r.handle, telemetry_consent: r.telemetry_consent || 0 }
}

export async function setTelemetryConsent(token: string, rid: string, consent: boolean) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  if (state.researchers[realRid]) {
    state.researchers[realRid].telemetry_consent = consent ? 1 : 0
    state.researchers[realRid].updated_at = Date.now()
    recordEvent(state, realRid, 'consent.updated', { consent: consent ? 1 : 0 })
    saveState(state)
  }
  return { ok: true }
}

export async function listCycles(token: string, rid: string) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  const list = Object.values(state.cycles)
    .filter((c: any) => c.researcher_id === realRid)
    .sort((a: any, b: any) => (b.created_at || 0) - (a.created_at || 0))
  return list as Cycle[]
}

export async function createCycle(token: string, rid: string, claim: { title: string; source_hint?: string; excerpt?: string }) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  const id = nanoid(10)
  const now = Date.now()
  const c = { id, researcher_id: realRid, status: 'active', created_at: now, updated_at: now }
  state.cycles[id] = c
  const claimId = nanoid(10)
  state.claims[claimId] = {
    id: claimId, cycle_id: id, title: (claim.title || '').slice(0, 280),
    source_hint: claim.source_hint || null, excerpt: claim.excerpt || null, created_at: now
  }
  recordEvent(state, realRid, 'cycle.created', { id })
  saveState(state)
  return { id }
}

export async function getFullCycle(token: string, rid: string, id: string) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  const cycle = state.cycles[id]
  if (!cycle || cycle.researcher_id !== realRid) throw new Error('not_found')
  const claim = Object.values(state.claims).find((cl: any) => cl.cycle_id === id) as any
  const prediction = state.predictions[id] || null
  const action = state.actions[id] || null
  const observation = state.observations[id] || null
  const update = state.updates[id] || null
  return { ...cycle, claim: claim || null, prediction: prediction || null, action: action || null, observation: observation || null, update: update || null } as FullCycle
}

export async function sealCycle(token: string, rid: string, id: string) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  if (state.cycles[id] && state.cycles[id].researcher_id === realRid) {
    const now = Date.now()
    state.cycles[id].status = 'sealed'
    state.cycles[id].sealed_at = now
    state.cycles[id].updated_at = now
    recordEvent(state, realRid, 'cycle.sealed', { id })
    saveState(state)
  }
  return { ok: true }
}

export async function savePrediction(token: string, rid: string, cycleId: string, p: { helplessness: number; predicted_resolution: string; key_assumptions?: string[] }) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  if (!state.cycles[cycleId] || state.cycles[cycleId].researcher_id !== realRid) throw new Error('not_found')
  const now = Date.now()
  state.predictions[cycleId] = {
    id: nanoid(10), cycle_id: cycleId, helplessness: p.helplessness,
    predicted_resolution: p.predicted_resolution || '', key_assumptions_json: p.key_assumptions ? JSON.stringify(p.key_assumptions) : null, created_at: now
  }
  state.cycles[cycleId].updated_at = now
  state.cycles[cycleId].helplessness_before = p.helplessness
  recordEvent(state, realRid, 'prediction.saved', { cycle: cycleId })
  saveState(state)
  return { ok: true }
}

export async function saveAction(token: string, rid: string, cycleId: string, a: { action_type: string; action_note: string; effort_minutes?: number }) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  if (!state.cycles[cycleId] || state.cycles[cycleId].researcher_id !== realRid) throw new Error('not_found')
  const now = Date.now()
  state.actions[cycleId] = {
    id: nanoid(10), cycle_id: cycleId, action_type: a.action_type,
    action_note: a.action_note || '', effort_minutes: a.effort_minutes || null, created_at: now
  }
  state.cycles[cycleId].updated_at = now
  recordEvent(state, realRid, 'action.saved', { cycle: cycleId })
  saveState(state)
  return { ok: true }
}

export async function saveObservation(token: string, rid: string, cycleId: string, o: { findings: string; surprise_level: number; sources_found?: number; discrepancy_note?: string }) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  if (!state.cycles[cycleId] || state.cycles[cycleId].researcher_id !== realRid) throw new Error('not_found')
  const now = Date.now()
  state.observations[cycleId] = {
    id: nanoid(10), cycle_id: cycleId, findings: o.findings, surprise_level: o.surprise_level,
    sources_found: o.sources_found || null, discrepancy_note: o.discrepancy_note || null, created_at: now
  }
  state.cycles[cycleId].updated_at = now
  state.cycles[cycleId].surprise_level = o.surprise_level
  recordEvent(state, realRid, 'observation.saved', { cycle: cycleId })
  saveState(state)
  return { ok: true }
}

export async function saveUpdate(token: string, rid: string, cycleId: string, u: { error_types: string[]; blame_vector: number; system_factors: string; learning_extraction: string; next_time: string }) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  if (!state.cycles[cycleId] || state.cycles[cycleId].researcher_id !== realRid) throw new Error('not_found')
  const now = Date.now()
  const errorJson = JSON.stringify(u.error_types || [])
  const blame = Math.max(-2, Math.min(2, u.blame_vector | 0))
  const sysRec = Math.max(0, Math.min(10, (u.system_factors || '').length > 50 ? 7 : 4) + (blame > 0 ? 1 : 0))
  state.updates[cycleId] = {
    id: nanoid(10), cycle_id: cycleId, error_types_json: errorJson, blame_vector: blame,
    system_factors: u.system_factors || '', learning_extraction: u.learning_extraction || '',
    next_time: u.next_time || '', created_at: now
  }
  // update denorm + tol (simplified proxy like worker)
  const pred = state.predictions[cycleId]
  const obs = state.observations[cycleId]
  const hBefore = pred?.helplessness ?? 5
  const hAfter = Math.max(0, Math.min(10, hBefore - 2 + Math.floor(Math.random() * 2)))
  const tol = computeToleranceIndex(hBefore, hAfter, sysRec, obs?.surprise_level || 4, (u.learning_extraction || '').length)
  const c = state.cycles[cycleId]
  c.updated_at = now
  c.helplessness_after = hAfter
  c.system_recognition = sysRec
  c.blame_vector = blame
  c.error_types_json = errorJson
  c.tolerance_index = tol
  recordEvent(state, realRid, 'update.saved', { cycle: cycleId })
  saveState(state)
  return { ok: true }
}

export async function getAISuggestions(token: string, rid: string, cycleId: string, phase: 'predict' | 'update', context: any) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  // Graceful mock (real AI only when worker + AI binding provisioned)
  let suggestions: any
  if (phase === 'predict') {
    suggestions = {
      predict: ["availability", "single_source", "catastrophizing"],
      note: "Mock suggestions (local deploy). Full Workers AI available in complete setup."
    }
  } else {
    suggestions = {
      update: { errors: ["single_source", "omitted_context"], system: "Media incentives favor dramatic claims.", learning: "Cross-check primary sources first." },
      note: "Mock (local deploy)."
    }
  }
  state.aiSuggestions[cycleId + phase] = suggestions
  saveState(state)
  return { suggestions }
}

export async function listEvents(token: string, rid: string) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  return state.events.filter(e => e.researcher_id === realRid).slice(0, 80) as Event[]
}

export async function listExports(token: string, rid: string) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  return Object.values(state.exportsRec).filter((e: any) => e.researcher_id === realRid) as ExportRecord[]
}

export async function exportCycle(token: string, rid: string, cycleId: string, method: string) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  const now = Date.now()
  const expId = nanoid(10)
  const rec = { id: expId, researcher_id: realRid, cycle_id: cycleId, kind: 'cycle_transcript', method, created_at: now }
  state.exportsRec[expId] = rec
  recordEvent(state, realRid, 'cycle.exported', { cycle: cycleId, method })
  saveState(state)
  // For static: always succeed; self_html is handled client in App. Other methods fall back gracefully.
  return { id: expId, method, url: method === 'self_html' ? undefined : '#local-export' }
}

export async function revokeExport(token: string, rid: string, exportId: string) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  if (state.exportsRec[exportId]) delete state.exportsRec[exportId]
  saveState(state)
  return { ok: true }
}

export async function triggerErasure(token: string, rid: string, confirmation: string) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  if (!confirmation || confirmation.length < 30) throw new Error('confirmation_phrase_required')
  // Local clear for this rid
  Object.keys(state.cycles).forEach(k => { if (state.cycles[k].researcher_id === realRid) delete state.cycles[k] })
  Object.keys(state.claims).forEach(k => { if (state.claims[k].cycle_id && state.cycles[state.claims[k].cycle_id]?.researcher_id === realRid) delete state.claims[k] })
  // clear related...
  ;['predictions','actions','observations','updates'].forEach(tbl => {
    const t = (state as any)[tbl]
    Object.keys(t).forEach(k => { if (t[k].cycle_id && state.cycles[t[k].cycle_id]?.researcher_id !== realRid) {} else if (t[k].researcher_id === realRid || (t[k].cycle_id && !state.cycles[t[k].cycle_id])) delete t[k] })
  })
  delete state.researchers[realRid]
  // keep sessions minimal
  saveState(state)
  const jobId = nanoid(8)
  return { job_id: jobId, status: 'started' }
}

export async function downloadFullArchive(token: string, rid: string) {
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  const researcher = state.researchers[realRid]
  const cycles = Object.values(state.cycles).filter((c: any) => c.researcher_id === realRid)
  const events = state.events.filter((e: any) => e.researcher_id === realRid)
  const archive = { exported_at: Date.now(), researcher, cycles, events }
  const blob = new Blob([JSON.stringify(archive, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'ambit-full-archive.json'
  document.body.appendChild(a); a.click(); a.remove()
  URL.revokeObjectURL(url)
}

export async function submitTelemetryConsentPoint(token: string, rid: string, point: TelemetryPoint) {
  // local only: store in events as "telemetry.local" if consent
  const state = loadState()
  const realRid = getRidFrom(token, rid)
  const r = state.researchers[realRid]
  if (r && r.telemetry_consent) {
    recordEvent(state, realRid, 'telemetry.local', { topic: point.topic })
    saveState(state)
  }
}
